import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { useUiStore } from "@/store/ui";
import { useFetchAgendamientos } from "@/shared/actions/erp/operaciones/agendamiento/agendamiento.actions";
import { putTrasladoSupervision } from "@/shared/actions/orden-servicio/traslado-supervision.actions";
import { OrdenServicio } from "../../types";

type Props = {
  open: boolean;
  onClose: () => void;
  orden: OrdenServicio | null;
  onSaved?: () => void;
};

function fmtFecha(v: any): string {
  if (!v) return "—";
  const d = dayjs(String(v));
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : "—";
}

/** Celda label/valor de SOLO LECTURA (contexto, no editable). */
function Dato({ label, value, sm = 3 }: { label: string; value?: any; sm?: number }) {
  return (
    <Grid item xs={6} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </Typography>
    </Grid>
  );
}

/**
 * Editor de SUPERVISIÓN de un traslado EN ESPERA. Réplica de la vista de creación
 * pero con la MAYORÍA de campos en SOLO LECTURA (contexto de orden, ubicación
 * actual, datos del cliente, coordenadas/flota/fecha). Editable: dirección de la
 * nueva ubicación, valores/recaudación (con validaciones como en creación) y
 * observaciones. Cada cambio queda en la auditoría del traslado.
 */
export default function ModalEditarTrasladoSupervision({ open, onClose, orden, onSaved }: Props) {
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const t: any = orden?.traslado || {};

  // Fuente de contexto = el AGENDAMIENTO (Django), que snapshotea contrato/cliente
  // (funciona también con contratos Softnet que no están en base_erp) y trae la
  // flota con su nombre (técnico) y la fecha real del agendamiento.
  const { data: agRes } = useFetchAgendamientos({
    enabled: !!orden?.id_orden_servicio,
    params: { referencia_orden_servicio: orden?.id_orden_servicio, filterByState: false } as any,
  });
  // La respuesta viene como { data: { data: { count, data: [...] } } }.
  const ag: any =
    (agRes as any)?.data?.data?.data?.[0] ||
    (agRes as any)?.data?.data?.[0] ||
    {};
  const c: any = ag.contrato_data || {};
  const cli: any = ag.cliente_data || {};
  // Nombre de la flota (= técnico, la flota se nombra por su líder).
  const flotaNombre =
    ag?.flota_data?.nombre || ag?.flota_data?.lider_data?.razon_social || "";
  // Fecha/hora real del agendamiento (fallback a la del traslado).
  const fechaAgenda =
    ag?.fecha_instalacion
      ? `${ag.fecha_instalacion}${ag?.hora_instalacion ? " " + String(ag.hora_instalacion).slice(0, 5) : ""}`
      : fmtFecha(t.fecha_traslado);

  const initial = useMemo(
    () => ({
      direccion: t.direccion ?? "",
      valor_traslado: t.valor_traslado ?? "",
      recaudacion_tecnico: !!t.recaudacion_tecnico,
      cargo_a_factura: !!t.cargo_a_factura,
      meses_pago: t.meses_pago != null ? String(t.meses_pago) : "1",
      fibrapoints: (t.puntos_canjeados ?? 0) > 0,
      observaciones: t.observaciones ?? "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orden?.id_orden_servicio],
  );

  const [f, setF] = useState(initial);
  const set = (patch: Partial<typeof f>) => setF((prev) => ({ ...prev, ...patch }));

  const idTraslado = t.id_traslado as string | undefined;

  const valorNum = Number(String(f.valor_traslado ?? "").replace(",", ".")) || 0;
  const mesesNum = f.cargo_a_factura ? Math.max(1, Math.floor(Number(f.meses_pago) || 1)) : 1;
  const cuotaMensual = mesesNum > 0 ? valorNum / mesesNum : valorNum;
  const mostrarResumenPago = !f.fibrapoints && (f.recaudacion_tecnico || f.cargo_a_factura) && valorNum > 0;
  const puntosDescuento = (t.puntos_canjeados ?? 0) > 0 ? t.puntos_canjeados : 1200;

  const promocionesTxt = Array.isArray(c.promociones)
    ? c.promociones.map((p: any) => p?.nombre || p).filter(Boolean).join(", ")
    : c.promociones;

  const handleGuardar = async () => {
    if (!idTraslado) {
      toast.error("No se encontró el id del traslado.");
      return;
    }
    if (!f.fibrapoints && !f.recaudacion_tecnico && !f.cargo_a_factura) {
      toast.error("Selecciona una opción de recaudación: ¿Recaudación técnico? o ¿Cargo a factura?");
      return;
    }
    setIsGlobalLoading(true);
    try {
      const payload = {
        direccion: f.direccion,
        observaciones: f.observaciones,
        valor_traslado: f.fibrapoints ? "0" : f.valor_traslado,
        recaudacion_tecnico: f.fibrapoints ? false : f.recaudacion_tecnico,
        cargo_a_factura: f.fibrapoints ? false : f.cargo_a_factura,
        meses_pago: f.cargo_a_factura && !f.fibrapoints ? Number(f.meses_pago) || 1 : null,
      };
      const res = await putTrasladoSupervision(idTraslado, payload);
      toast.success(res?.message || "Traslado actualizado correctamente");
      onSaved?.();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "No se pudo actualizar el traslado");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const seccion = (titulo: string) => (
    <>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {titulo}
      </Typography>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
        <EditNoteOutlinedIcon color="primary" />
        Editar traslado (supervisión)
        {orden?.numero_orden_servicio ? ` — ${orden.numero_orden_servicio}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {/* ---- Información de la orden (solo lectura) ---- */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Información de la orden
        </Typography>
        <Grid container spacing={2}>
          <Dato label="N° Orden" value={orden?.numero_orden_servicio || orden?.id_orden_servicio?.slice(0, 8)} />
          <Dato label="Tipo" value={orden?.tipo_orden_servicio} />
          <Dato label="Estado" value={orden?.estado} />
          <Dato label="Contrato" value={orden?.contrato} />
          <Dato label="Creado por" value={orden?.user_create} sm={6} />
          <Dato label="Última modificación" value={fmtFecha(orden?.fechaModificacion)} sm={6} />
        </Grid>

        {/* ---- Ubicación Actual del Servicio (solo lectura, del contrato) ---- */}
        {seccion("Ubicación Actual del Servicio")}
        <Grid container spacing={2}>
          <Dato label="Coordenadas" value={c.coordenadas} sm={6} />
          <Dato label="Dirección" value={c.direccion} sm={6} />
          <Dato label="Provincia" value={c.provincia ?? c.provincia_data?.nombre} />
          <Dato label="Ciudad" value={c.ciudad ?? c.ciudad_data?.nombre} />
          <Dato label="Parroquia" value={c.sector ?? c.sector_data?.nombre} />
          <Dato label="Zona" value={c.zona ?? c.zona_data?.nombre} />
        </Grid>

        {/* ---- Datos del Cliente (solo lectura) ---- */}
        {seccion("Datos del Cliente")}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.5, letterSpacing: 0.5 }}>
          DATOS PERSONALES
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Dato label="Tipo Documento" value={cli.tipo_identificacion} />
          <Dato label="Identificación" value={cli.identificacion ?? c.identificacion} />
          <Dato label="Nombre Cliente" value={cli.razon_social ?? c.razon_social} sm={6} />
          <Dato label="Correo Electrónico" value={cli.email ?? c.email} sm={6} />
          <Dato label="Teléfono Móvil" value={cli.celular ?? c.celular} />
        </Grid>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.5, letterSpacing: 0.5 }}>
          DATOS COMERCIALES
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Dato label="Plan" value={c.plan ?? c.plan_data?.nombre} />
          <Dato label="Velocidad" value={c.velocidad ? `${c.velocidad} Mbps` : undefined} />
          <Dato label="Costo Mensual" value={c.valor_plan ? `$${c.valor_plan}` : undefined} />
          <Dato label="Tipo Servicio" value={c.tipo_servicio} />
        </Grid>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.5, letterSpacing: 0.5 }}>
          PROMOCIONES Y PAGO
        </Typography>
        <Grid container spacing={2}>
          <Dato label="Promociones" value={promocionesTxt} sm={6} />
          <Dato label="Forma de Pago" value={c.forma_pago ?? c.forma_pago_data?.nombre} sm={6} />
          <Dato label="Saldo Pendiente" value={c.saldo_pendiente != null ? `$${c.saldo_pendiente}` : undefined} />
          <Dato label="Fibra Points" value={c.fibra_points ?? c.fibrapoints} />
        </Grid>

        {/* ---- Nueva ubicación (dirección editable, coordenadas lectura) ---- */}
        {seccion("Nueva ubicación")}
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Dirección"
              value={f.direccion} onChange={(e) => set({ direccion: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Coordenadas"
              value={t.coordenada || ""} disabled
              InputProps={{ endAdornment: <LockOutlinedIcon fontSize="small" color="disabled" /> }}
            />
          </Grid>
        </Grid>

        {/* ---- Detalle de valores (editable + validaciones como creación) ---- */}
        {seccion("Detalle de valores")}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" type="number" label="Valor del traslado"
              value={f.valor_traslado} onChange={(e) => set({ valor_traslado: e.target.value })}
              disabled={f.fibrapoints}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <FormControlLabel
              control={<Checkbox checked={f.fibrapoints} onChange={(e) => set({ fibrapoints: e.target.checked })} />}
              label="¿Traslado por FibraPoints?"
            />
            {f.fibrapoints && (
              <Typography variant="caption" color="warning.main" fontWeight={700} sx={{ display: "block" }}>
                Pago por FibraPoints: se descontarán {puntosDescuento} FibraPoints (valor $0, sin recaudación).
              </Typography>
            )}
          </Grid>
          {!f.fibrapoints && (
            <>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={f.recaudacion_tecnico}
                      onChange={(e) =>
                        set({ recaudacion_tecnico: e.target.checked, cargo_a_factura: e.target.checked ? false : f.cargo_a_factura })
                      }
                    />
                  }
                  label="¿Recaudación técnico?"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={f.cargo_a_factura}
                      onChange={(e) =>
                        set({ cargo_a_factura: e.target.checked, recaudacion_tecnico: e.target.checked ? false : f.recaudacion_tecnico })
                      }
                    />
                  }
                  label="¿Cargo a factura?"
                />
              </Grid>
              {f.cargo_a_factura && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" type="number" label="Meses a diferir"
                    value={f.meses_pago} onChange={(e) => set({ meses_pago: e.target.value })}
                    helperText="1 = una cuota; más = diferido"
                  />
                </Grid>
              )}
              {!f.recaudacion_tecnico && !f.cargo_a_factura && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    Selecciona una opción de recaudación.
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {mostrarResumenPago && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
                  Aproximado a pagar por el cliente
                </Typography>
                {f.cargo_a_factura ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">Valor del traslado</Typography>
                      <Typography variant="body2" fontWeight={600}>${valorNum.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">Meses a diferir</Typography>
                      <Typography variant="body2" fontWeight={600}>{mesesNum} {mesesNum === 1 ? "mes" : "meses"}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" fontWeight={700}>Cuota mensual aprox.</Typography>
                      <Chip label={`$${cuotaMensual.toFixed(2)} / mes`} color="primary" sx={{ fontWeight: 800 }} />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" fontWeight={700}>Total a recaudar por el técnico</Typography>
                    <Chip label={`$${valorNum.toFixed(2)}`} color="primary" sx={{ fontWeight: 800 }} />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" fontStyle="italic" sx={{ display: "block", mt: 1 }}>
                  * Cálculo referencial. Puede variar según impuestos o ajustes de facturación.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* ---- Agendamiento (solo lectura) + Observaciones (editable) ---- */}
        {seccion("Agendamiento y observaciones")}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Flota / técnico"
              value={flotaNombre || "—"} disabled
              InputProps={{ endAdornment: <LockOutlinedIcon fontSize="small" color="disabled" /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Fecha del traslado"
              value={fechaAgenda || "—"} disabled
              InputProps={{ endAdornment: <LockOutlinedIcon fontSize="small" color="disabled" /> }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Observaciones para el técnico"
              value={f.observaciones} onChange={(e) => set({ observaciones: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleGuardar} variant="contained">
          Guardar cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
}
